// Shared axios client — single source of truth for auth + base URL.
//
// Why this exists:
// 63 components were manually setting `Authorization: Bearer ${token}` on every
// request. That meant: (a) every new endpoint had to remember to wire auth,
// (b) refreshing the token in one place didn't propagate, (c) impossible to
// add cross-cutting concerns (logging, retry, 401-handling) without touching
// every file.
//
// Migration plan (incremental, non-breaking):
// - New code: `import api from '../lib/apiClient'` then `api.get('/users/me/pulse')`.
// - Old code: still works as-is (regular `axios` + manual headers). Migrate as
//   you touch each file. Both patterns can coexist forever.
//
// The interceptor reads localStorage on every request, so token refreshes after
// login/logout are picked up automatically without a re-render.

import axios from 'axios';
import { API } from '../config/api';
import { getStoredStepupToken, openTwofaChallenge, clearStepupToken } from './twofaChallenge';

const api = axios.create({
  baseURL: API,
  // Don't set a global timeout — uploads need minutes, polling needs seconds.
  // Components with strict latency budgets pass `timeout` per call.
});

api.interceptors.request.use((config) => {
  // Pull the freshest token on every call. AuthContext writes to this exact key.
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem('globaleyes_token');
    if (token) {
      config.headers = config.headers || {};
      // Don't clobber an explicitly-set Authorization (e.g. magic-link claim).
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    // Attach the 2FA stepup token if we have a fresh one. Some endpoints
    // (admin, change-password) require it; for everything else the server
    // simply ignores the header.
    const stepup = getStoredStepupToken();
    if (stepup && config.headers && !config.headers['X-2FA-Token']) {
      config.headers['X-2FA-Token'] = stepup;
    }
  }
  return config;
});

// Response interceptor — when a request comes back with
// 401 {detail: '2fa_stepup_required'}, prompt the user for a TOTP code
// and retry. 403 {detail: 'enroll_2fa_required'} redirects to enrollment.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const original = error?.config;

    // Admin without 2FA enrolled hit a protected endpoint — bounce them
    // to the Security tab. Hard redirect so any in-flight axios chains
    // unwind cleanly.
    //
    // Scope guard (added iter-47): only fire the redirect when the
    // failing call is an analytics endpoint. Otherwise, calls from
    // unrelated admin features (e.g. ChairmansBazooka, inbox triage)
    // that *don't* require 2FA stepup but happen to be co-mounted on a
    // page that *does* fetch analytics would forcibly unmount the
    // admin UI mid-interaction. Let the offending caller deal with the
    // 403 in its own scope.
    if (status === 403 && detail === 'enroll_2fa_required') {
      const url = (original?.url || '').toString();
      const analyticsLike = (
        url.includes('/admin/analytics') ||
        url.includes('/admin/top-creators') ||
        url.includes('/admin/top-videos') ||
        url.includes('/admin/regions') ||
        url.includes('/admin/users-paged')
      );
      if (analyticsLike) {
        try {
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/dashboard')) {
            window.location.href = '/dashboard?tab=security';
          }
        } catch (_) { /* noop */ }
      }
      return Promise.reject(error);
    }

    if (status === 401 && detail === '2fa_stepup_required' && original && !original._twofaRetried) {
      original._twofaRetried = true;
      // Stored token may be stale; clear before challenging.
      clearStepupToken();
      try {
        const fresh = await openTwofaChallenge();
        original.headers = original.headers || {};
        original.headers['X-2FA-Token'] = fresh;
        return api.request(original);
      } catch (_) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
